import { CommonService } from './../common/common.service';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponseBase,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, firstValueFrom, map, throwError } from 'rxjs';
import { ErrorMessageConstants } from 'src/app/constants/error-message-constants';
import { RouteConstants } from 'src/app/constants/route-constants';
import { ApiEndPoints } from 'src/app/constants/web-constants';
import { AuthenticationInterface } from 'src/app/models/auth/authentication-interface';
import { DataInterface } from 'src/app/models/web/data-interface';
import { DataListInterface } from 'src/app/models/web/data-list-interface';
import { StatusInterface } from 'src/app/models/web/status-interface';
import { AppSessionStorageService } from 'src/app/services/utils/session-storage.service';
import { WebServiceMethod } from './web-method';
import { WebServiceModel } from './web-service-model';
import { EncryptKeyModel } from 'src/app/models/web/encrypt-key.model';
import { Utils } from 'src/app/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class WebService {
  private static readonly serverErrorCodes = [500];
  private static isFernetKeyFetching: boolean = false;

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    public router: Router,
    private sessionStorage: AppSessionStorageService
  ) {}

  /// Api to generate cookie
  async callKeyGenerateApi(forceCall: boolean = false): Promise<boolean> {
    if (
      (!this.commonService.isEncryptionKeyFetched() || forceCall) &&
      !WebService.isFernetKeyFetching
    ) {
      try {
        WebService.isFernetKeyFetching = true;
        const requestModel = new WebServiceModel(
          ApiEndPoints.SET_COOKIE,
          WebServiceMethod.Get
        );
        const response = await this.single<EncryptKeyModel>(requestModel);

        if (response.success) {
          const encryptKeyModel = response.data;
          await this.commonService.updateKey(encryptKeyModel);
          this.recallKeyGenerate(1 * 60 * 60 * 1000, true);
          WebService.isFernetKeyFetching = false;
          return true;
        } else if(response.responseCode!=422){
          return await this.recallKeyGenerate(3000);
        }
      } catch (_) {
        return await this.recallKeyGenerate(3000);
      }
    }
    WebService.isFernetKeyFetching = false;
    return this.commonService.isEncryptionKeyFetched();
  }

  private async recallKeyGenerate(
    delay: number,
    forceCall: boolean = false
  ): Promise<boolean> {
    await Utils.delay(delay);
    WebService.isFernetKeyFetching = false;
    return await this.callKeyGenerateApi(forceCall);
  }

  private setError(
    responseCode: number,
    response: any,
    defaultMessage = ErrorMessageConstants.SOMETHING_WRONG
  ): string {
    try {
      if (WebService.serverErrorCodes.indexOf(responseCode) > -1) {
        return ErrorMessageConstants.SOMETHING_WRONG;
      }

      if (response instanceof HttpErrorResponse) {
        const status: StatusInterface = response.error;
        return status.message;
      }
    } catch (_) {}
    return defaultMessage;
  }

  private handleSuccess(res: Response | undefined | null) {
    let body = res;
    return {
      responseCode: res?.status ?? 200,
      success: true,
      data: body,
      message: '',
    };
  }

  /*
   * The error will show on console now. Need to hadle it
   */
  private handleError(error: any) {
    return throwError(() => error);
  }

  private handleImageSuccess(res: Blob) {
    let body = res;
    return {
      responseCode: 200,
      success: true,
      data: body,
      message: '',
    };
  }

  private async assignValuesToFormData(model: WebServiceModel) {
    const needEncryption = ApiEndPoints.isNeedEncryption(model.url, model.method);
  
    if (model.useFormData && model.isDataIsJsonType && model.data) {
      await this.appendDataToFormData(model, model.data, needEncryption);
    } else if (!model.useFormData && needEncryption && model.data) {
      model.data = { data: await this.encrypt(model.data) };
    }
  }
  
  private async appendDataToFormData(model: WebServiceModel, data: any, needEncryption: boolean) {
    const assign: any = { ...data };
    const keys = Object.keys(assign);
  
    for (const key of keys) {
      if (Array.isArray(assign[key])) {
        await this.appendArrayToFormData(model, key, assign[key], needEncryption);
      } else {
        model.formData.append(`${key}`, `${needEncryption ? await this.encrypt(assign[key]) : assign[key]}`);
      }
    }
  }
  
  private async appendArrayToFormData(model: WebServiceModel, key: string, array: any[], needEncryption: boolean) {
    for (const val of array) {
      model.formData.append(`${key}`, `${needEncryption ? await this.encrypt(val) : val}`);
    }
  }
  

  private encrypt(data: any): Promise<string> {
    return this.commonService.encrypt(data);
  }

  private decrypt(data: any): Promise<any> {
    return this.commonService.decryptData(data);
  }

  private httpCall(model: WebServiceModel): Observable<Response> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if(model.useFormData){
      headers = new HttpHeaders({});
    }
    switch (model.method) {
      case WebServiceMethod.Post:
        return this.http.post<Response>(
          model.url,
          model.useFormData ? model.formData : model.data,
          { headers }
        );

      case WebServiceMethod.Put:
        return this.http.put<Response>(
          model.url,
          model.useFormData ? model.formData : model.data,
          { headers }
        );
      case WebServiceMethod.Delete:
        return this.http.delete<Response>(model.url, { body: model.data });
      case WebServiceMethod.Patch:
        return this.http.patch<Response>(
          model.url,
          model.useFormData ? model.formData : model.data,
          { headers }
        );

      default:
        return this.http.get<Response>(model.url, {
          params: model.data ?? {},
          headers: {
            responseType: 'json',
          },
        });
    }
  }

  private async call(
    model: WebServiceModel,
    callRefreshIfExpired: boolean = true
  ): Promise<any> {
    try {
      await this.assignValuesToFormData(model);
      if (model.method == WebServiceMethod.Image) {
        return await firstValueFrom(
          this.http
            // load the image as a blob
            .get(model.url, { responseType: 'blob' })
            .pipe(
              // create an object url of that blob that we can use in the src attribute
              map(this.handleImageSuccess),
              catchError(this.handleError)
            )
        );
      }
      const call = this.httpCall(model);
      return await firstValueFrom(
        call.pipe(map(this.handleSuccess), catchError(this.handleError))
      );
    } catch (e) {
      return await this.onApiError(e, model, callRefreshIfExpired);
    }
  }

  private async onApiError(
    e: any,
    model: WebServiceModel,
    callRefreshIfExpired: boolean
  ): Promise<any> {
    const isNetworkError = e instanceof HttpErrorResponse;
    const res = await this.checkIsNetworkError(
      isNetworkError,
      e,
      model,
      callRefreshIfExpired
    );
    if (res) {
      return res;
    }
    const responseCode = e instanceof HttpResponseBase ? e.status : 400;
    return {
      success: false,
      data: undefined,
      isNetworkError: isNetworkError,
      responseCode: responseCode,
      message: this.setError(responseCode, e),
      error: this.setError(responseCode, e),
    };
  }

  private async checkIsNetworkError(
    isNetworkError: boolean,
    e: any,
    model: WebServiceModel,
    callRefreshIfExpired: boolean
  ): Promise<any> {
    if (isNetworkError) {
      if (this.isResponseCodeIsForRefreshToken(e)) {
        if (this.isRefreshCallRequired(model, callRefreshIfExpired)) {
          const response = await this.refreshTheToken();
          if (response.success && response.data) {
            return await this.call(model, false);
          }
        }
        this.logout();
      }
    }
    return undefined;
  }

  private isResponseCodeIsForRefreshToken(e: any): boolean {
    return e.status === 401 && AppSessionStorageService.isLoggedIn();
  }

  private logout() {
    if (AppSessionStorageService.isLoggedIn()) {
      this.sessionStorage.removeToken();
      localStorage?.clear();
      this.router.navigate([RouteConstants.SIGNIN], {
        replaceUrl: true,
      });
    }
  }

  private isRefreshCallRequired(
    model: WebServiceModel,
    callRefreshIfExpired: boolean
  ): boolean {
    return model.url != ApiEndPoints.REFRESH_TOKEN && callRefreshIfExpired;
  }

  async direct(url: string): Promise<any> {
    const call = this.http.get<any>(url);
    return await firstValueFrom(
      call.pipe(map(this.handleSuccess), catchError(this.handleError))
    );
  }

  async status(model: WebServiceModel): Promise<StatusInterface> {
    const res = await this.call(model);
    return res as StatusInterface;
  }

  async single<T>(model: WebServiceModel): Promise<DataInterface<T>> {
    const res = await this.call(model);
    if (res.success && ApiEndPoints.isNeedDecryption(model.url,model.method) && res.data) {
      res.data = await this.decrypt(res.data);
    }
    return res as DataInterface<T>;
  }

  async list<T>(model: WebServiceModel): Promise<DataListInterface<T>> {
    const res = await this.call(model);
    if (res.success && ApiEndPoints.isNeedDecryption(model.url,model.method) && res.data) {
      res.data = await this.decrypt(res.data);
    }
    return res as DataListInterface<T>;
  }

  async loadImage(
    url: string,
    needAuthToken: boolean = true
  ): Promise<DataInterface<string | null>> {
    const model = new WebServiceModel(url, WebServiceMethod.Image);
    model.needToken = needAuthToken;
    return this.single(model);
  }

  async refreshTheToken(): Promise<DataInterface<AuthenticationInterface>> {
    const refreshTokenModel = new WebServiceModel(
      ApiEndPoints.REFRESH_TOKEN,
      WebServiceMethod.Get
    );
    const response = await this.single<AuthenticationInterface>(
      refreshTokenModel
    );
    if (response.success && response.data) {
      this.sessionStorage.setToken(
        response.data,
        this.sessionStorage.shouldRememberToken()
      );
    }
    return response;
  }
}
